import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Input, Button, Form, message, Select, Switch, Image } from "antd";
import type { MenuProps } from "antd";
import { RcFile } from "antd/es/upload/interface";
import { v4 as uuidv4 } from "uuid";
import { ApolloError } from "@apollo/client";

import { useGetOrganizationByIdQuery } from "graphql/queries/get-organization-by-id.generated";
import { useUpdateOrganizationByIdMutation } from "graphql/mutations/update_organization_by_id.generated";
import { Organizations_Set_Input, Properties } from "graphql/generated/graphql";
import { useGetPropertiesByOrganizationIdQuery } from "graphql/queries/get-properties-by-organization-id.generated";
import { useGetJurisdictionsQuery } from "graphql/queries/get-jurisdictions.generated";
import { useUpdatePropertiesManyMutation } from "graphql/mutations/update_properties.generated";

import LoadingOverlay from "components/loading-overlay/loading-overlay";
import UploadPhotoModal from "components/upload-photo-modal/upload-photo-modal";
import { useNavProfile } from "hooks/useNavProfile";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import routeNames from "constants/route-names";
import { compareEntities } from "utils/helpers";
import { NavProfile, OrganizationType, UserRoleValue } from "constants/field-values";
import { NAV_PROFILE_OPTIONS } from "constants/constants";
import { deleteFromStorage, uploadToStorage } from "utils/files-management";

const OrganizationEdit: React.FC = () => {
	const navigate = useNavigate();
	const params = useParams<{ organizationId: string }>();
	const { loggedUserValue } = useLoggedUser();
	const [name, setName] = useState<string>("");
	const [contactName, setContactName] = useState<string>("");
	const [email, setEmail] = useState<string>("");
	const [displayPrice, setDisplayPrice] = useState<boolean>(false);
	const [type, setType] = useState<OrganizationType | string>("");
	const [jurisdictionId, setJurisdictionId] = useState<string>("");
	const [navProfile, setNavProfile] = useState<string>("");

	// Only an Authority organization enforces a jurisdiction; it is what scopes its
	// users to the inspections and plan reviews they review. Reassigning it is a
	// platform-admin call: it moves the whole organization's access.
	const isAuthority = type === OrganizationType.Authority;
	const canEditJurisdiction = loggedUserValue.role === UserRoleValue.admin_user;

	const [propertiesDropdownOpen, setPropertiesDropdownOpen] = useState<boolean>(false);
	const [selectedPropertiesKeys, setSelectedPropertiesKeys] = useState<Array<string>>([]);
	const [selectedProperties, setSelectedProperties] = useState<Properties[]>([]);
	const [addOrgPhotoModalOpen, setAddOrgPhotoModalOpen] = useState<boolean>(false);
	const [orgPhotoThumbUrl, setOrgPhotoThumbUrl] = useState<string>("");
	const [orgPhotoFile, setOrgPhotoFile] = useState<RcFile | null>(null);
	const [orgPhotoUrl, setOrgPhotoUrl] = useState<string>("");
	const [uploading, setUploading] = useState<boolean>(false);
	const [organizationInputCompleted, setOrganizationInputCompleted] = useState<boolean>(false);

	const isAdminUser = loggedUserValue.role === UserRoleValue.admin_user;

	const { navProfile: activeNavProfile } = useNavProfile();
	const rolesWithoutPricingToggle = [
		UserRoleValue.customer_manager,
		UserRoleValue.customer_employee,
		UserRoleValue.authority_manager,
		UserRoleValue.authority_user,
		UserRoleValue.vendor_employee
	];
	const canSeePricingToggle =
		activeNavProfile.id !== NavProfile.Ollie && !rolesWithoutPricingToggle.includes(loggedUserValue.role as UserRoleValue);

	const isCurrentUserOrganization = useMemo(() => {
		return loggedUserValue.organization_id === params.organizationId || isAdminUser;
	}, [loggedUserValue.organization_id, params.organizationId, isAdminUser]);

	useEffect(() => {
		if (!isCurrentUserOrganization) {
			navigate(routeNames.ADMIN_ORGANIZATIONS);
		}
	}, [isCurrentUserOrganization]);

	const handleOnCompleteGetPropertiesByOrganizationId = useCallback(data => {
		// console.log("Get Properties By organization Id Complete data: ", data);
	}, []);

	const handleOnErrorGetPropertiesByOrganizationId = useCallback((error: ApolloError) => {
		console.log("Get Properties By organization Id Error: ", error);
	}, []);

	const { data: propertiesByOrganizationData, loading: propertiesByOrganizationLoading } =
		useGetPropertiesByOrganizationIdQuery({
			skip: !params.organizationId,
			variables: {
				organization_id: params.organizationId
			},
			errorPolicy: "none",
			fetchPolicy: "network-only",
			nextFetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorGetPropertiesByOrganizationId,
			onCompleted: handleOnCompleteGetPropertiesByOrganizationId
		});

	const handleOnErrorGetOrganizationById = useCallback((error: ApolloError) => {
		console.log("Get Organization By Id Error: ", error);
	}, []);

	const handleOnCompleteGetOrganizationBy = useCallback(data => {
		const {
			name,
			contact_name,
			email,
			type,
			jurisdiction_id,
			nav_profile,
			organizationImageURL,
			organization_id,
			org_properties = []
		} = data.organizations[0];

		console.log("Get organization By Id Complete data: ", data);

		const propertiesKeys = org_properties.map(element => element.id);

		setName(name || "");
		setContactName(contact_name || "");
		setEmail(email || "");
		setType(type);
		setJurisdictionId(jurisdiction_id || "");
		setNavProfile(nav_profile || "");
		setOrgPhotoThumbUrl(organizationImageURL || "");
		setSelectedProperties(org_properties);
		setSelectedPropertiesKeys(propertiesKeys);
	}, []);

	const { data: organizationData, loading: loadingOrganizationById } = useGetOrganizationByIdQuery({
		skip: !params.organizationId,
		variables: {
			id: params.organizationId
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorGetOrganizationById,
		onCompleted: handleOnCompleteGetOrganizationBy
	});

	const { data: jurisdictionsData, loading: loadingJurisdictions } = useGetJurisdictionsQuery({
		skip: !isAuthority,
		fetchPolicy: "cache-first",
		onError: useCallback((error: ApolloError) => console.log("getJurisdictions Error: ", error), [])
	});

	const handleOnCompleteUpdatePropertiesManyMutation = useCallback((data: any) => {
		// console.log("Complete Update Many Properties data: ", data);
	}, []);

	const handleOnErrorUpdatePropertiesManyMutation = useCallback((error: ApolloError) => {
		console.log("Update Many Properties Error: ", error);
	}, []);

	const [updatePropertiesManyMutation, { loading: loadingUpdateProperties }] = useUpdatePropertiesManyMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorUpdatePropertiesManyMutation,
		onCompleted: handleOnCompleteUpdatePropertiesManyMutation
	});

	const handleUpdateProperties = useCallback(async () => {
		const organization_id = params.organizationId;

		if (organizationData?.organizations[0]) {
			const { org_properties = [] } = organizationData.organizations[0];

			const currentProperties = org_properties;

			const comparison = compareEntities(currentProperties, selectedProperties);

			const propertiesToRemove = comparison.toRemove.map(property => {
				return {
					where: { id: { _eq: property.id } },
					_set: {
						organization_id: null
					}
				};
			});

			const propertiesToAdd = comparison.toAdd.map(property => {
				return {
					where: { id: { _eq: property.id } },
					_set: {
						organization_id: organization_id
					}
				};
			});

			const propertyUpdates = propertiesToAdd.concat(propertiesToRemove as any);

			if (propertyUpdates && propertyUpdates.length) {
				try {
					const result = await updatePropertiesManyMutation({
						variables: {
							updates: propertyUpdates
						}
					});
				} catch (err) {
					console.error("Error updating properties:", err);
				}
			}
		} else {
			if (organization_id && selectedProperties && selectedProperties.length) {
				const propertyUpdates = selectedProperties.map(property => {
					return {
						where: { id: { _eq: property.id } },
						_set: {
							organization_id: organization_id
						}
					};
				});

				try {
					const result = await updatePropertiesManyMutation({
						variables: {
							updates: propertyUpdates
						}
					});
				} catch (err) {
					console.error("Error updating properties:", err);
				}
			}
		}
	}, [params.organizationId, selectedProperties, organizationData]);

	const handleOnCompleteUpdateOrganizationMutation = useCallback((data: any) => {
		// console.log("Complete Update Organization data: ", data);
		navigate(routeNames.ADMIN_ORGANIZATIONS);
	}, []);

	const handleOnErrorUpdateOrganizationMutation = useCallback((error: ApolloError) => {
		console.log("Update Organization Error: ", error);
		navigate(routeNames.ADMIN_ORGANIZATIONS);
	}, []);

	const [updateOrganizationMutation, { data, loading, error }] = useUpdateOrganizationByIdMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorUpdateOrganizationMutation,
		onCompleted: handleOnCompleteUpdateOrganizationMutation
	});

	const isLoading =
		uploading || loading || loadingUpdateProperties || propertiesByOrganizationLoading || loadingOrganizationById;

	// const propertiesOptions = useMemo(() => {
	//   const { properties } = propertiesByOrganizationData || {};

	//   if (properties && properties.length) {
	//     const organizationProperties: MenuProps["items"] = properties.reduce(
	//       (options, property, currentindex, array) => {
	//         const optionId = `property-dropdown-option-${currentindex + 1}-${
	//           property?.id
	//         }`;

	//         const isSelected = selectedPropertiesKeys.includes(
	//           property?.id as never
	//         );

	//         const newOption = {
	//           key: `${property?.id}`,
	//           icon: (
	//             <div>
	//               <input
	//                 className="custom-checkbox"
	//                 id={optionId}
	//                 type="checkbox"
	//                 onClick={(e) => e.stopPropagation()}
	//                 checked={isSelected}
	//                 onChange={() => undefined}
	//               />
	//               <label htmlFor={optionId} className="custom-checkbox-label">
	//                 {property?.name}
	//               </label>
	//             </div>
	//           ),
	//         };

	//         options.push(newOption);

	//         return options;
	//       },
	//       [
	//         {
	//           key: "property-dropdown-option-all",
	//           icon: (
	//             <div>
	//               <input
	//                 className="custom-checkbox"
	//                 id="property-dropdown-option-all"
	//                 type="checkbox"
	//                 onClick={(e) => e.stopPropagation()}
	//               />
	//               <label
	//                 htmlFor="property-dropdown-option-all"
	//                 className="custom-checkbox-label"
	//               >
	//                 All properties
	//               </label>
	//             </div>
	//           ),
	//         },
	//       ]
	//     );

	//     return organizationProperties;
	//   }

	//   return [];
	// }, [propertiesByOrganizationData, selectedPropertiesKeys]);

	// const handleOnSelectProperties = useCallback(
	//   (args) => {
	//     const { selectedKeys } = args;
	//     const { properties } = propertiesByOrganizationData || {};

	//     if (selectedPropertiesKeys.includes("property-dropdown-option-all")) {
	//       if (selectedKeys.includes("property-dropdown-option-all")) {
	//         // select all is selected but the user unselect an option
	//         if (properties && properties.length + 1 !== selectedKeys.length) {
	//           const newSelectedProperties = properties?.filter((element) =>
	//             selectedKeys.includes(element.id)
	//           );

	//           const selectedKeysCopy = selectedKeys.filter(
	//             (element) => element !== "property-dropdown-option-all"
	//           );

	//           setSelectedProperties(newSelectedProperties as Array<never>);
	//           setSelectedPropertiesKeys(selectedKeysCopy);
	//           return;
	//         }
	//       } else {
	//         // select all is unselected
	//         setSelectedProperties([]);
	//         setSelectedPropertiesKeys([]);
	//         return;
	//       }
	//     } else {
	//       // select all is selected
	//       if (
	//         selectedKeys.includes("property-dropdown-option-all") &&
	//         properties &&
	//         properties.length
	//       ) {
	//         const propertiesKeys = properties?.map((element) => element.id) || [];
	//         const newPropertiesKeys = ["property-dropdown-option-all"].concat(
	//           propertiesKeys
	//         );

	//         setSelectedProperties(properties as Properties[]);
	//         setSelectedPropertiesKeys(newPropertiesKeys);
	//         return;
	//       }
	//     }

	//     const newSelectedProperties = properties?.filter((element) =>
	//       selectedKeys.includes(element.id)
	//     );

	//     setSelectedProperties(newSelectedProperties as Array<never>);
	//     setSelectedPropertiesKeys(selectedKeys);
	//   },
	//   [propertiesByOrganizationData, selectedPropertiesKeys]
	// );

	const uploadOrganizationImg = useCallback(async () => {
		if (orgPhotoFile) {
			try {
				setUploading(true);
				const uniqueID = uuidv4();

				const bucketName = "organization-photos/";

				const fileName = `organization-photo-${name}-${uniqueID}-${Math.floor(Date.now() / 1000)}-${orgPhotoFile.name}`;

				const { publicUrl } = await uploadToStorage(orgPhotoFile, () => {}, `${bucketName}/${fileName}`);

				setOrgPhotoUrl(publicUrl);
			} catch (error) {
				console.error("Error uploading file:", error);
				message.error("Failed to upload file.");
			} finally {
				setUploading(false);
			}
		}

		return;
	}, [orgPhotoFile, name]);

	const handleOnEditPhoto = useCallback(() => {
		setAddOrgPhotoModalOpen(true);
	}, []);

	const handleOnRemovePhoto = useCallback(async () => {
		const { organizationImageURL = "" } = organizationData?.organizations[0] || {};

		// Extract and store the storage path from the URL
		if (organizationImageURL) {
			// Firebase Storage URLs contain a token, we need to extract just the path
			try {
				await deleteFromStorage(organizationImageURL);

				// Update organization in database to remove the image URL
				await updateOrganizationMutation({
					variables: {
						set: {
							organizationImageURL: null
						},
						id: params.organizationId
					}
				});

				// Clear local state
				setOrgPhotoThumbUrl("");
				setOrgPhotoFile(null);

				message.success("Organization photo deleted successfully");
			} catch (error) {
				console.error("Error deleting organization photo:", error);
				message.error("Failed to delete organization photo");
			}
		}
	}, [organizationData, isLoading, params.organizationId, updateOrganizationMutation]);

	const handleOnConfirmPhoto = useCallback((file: any, url: string) => {
		setOrgPhotoThumbUrl(url);
		setOrgPhotoFile(file);
	}, []);

	const handleOnRemoveProperty = useCallback(
		propertyId => {
			const newSelectedProperties = selectedProperties?.filter(element => element.id !== propertyId);

			const newSelectedPropertiesKeys = selectedPropertiesKeys?.filter(element => element !== propertyId);

			setSelectedProperties(newSelectedProperties as Array<never>);
			setSelectedPropertiesKeys(newSelectedPropertiesKeys);
		},
		[selectedProperties, selectedPropertiesKeys]
	);

	const handleOnSubmit = useCallback(async () => {
		if (orgPhotoFile) {
			await uploadOrganizationImg();
		}

		await handleUpdateProperties();

		setOrganizationInputCompleted(true);
	}, [orgPhotoFile, uploadOrganizationImg, handleUpdateProperties]);

	const uploadOrganization = useCallback(async () => {
		let organizationInput: Organizations_Set_Input = {
			name,
			contact_name: contactName,
			email,
			display_price: displayPrice,
			type
		};

		if (orgPhotoUrl) {
			organizationInput.organizationImageURL = orgPhotoUrl;
		}

		if (isAuthority && canEditJurisdiction) {
			organizationInput.jurisdiction_id = jurisdictionId || null;
		}

		if (isAdminUser) {
			organizationInput.nav_profile = navProfile || null;
		}

		updateOrganizationMutation({
			variables: {
				set: organizationInput,
				id: params.organizationId
			}
		});
	}, [
		params.organizationId,
		isAuthority,
		canEditJurisdiction,
		jurisdictionId,
		isAdminUser,
		navProfile,
		name,
		contactName,
		email,
		displayPrice,
		type,
		loggedUserValue,
		orgPhotoUrl
	]);

	useEffect(() => {
		if (organizationInputCompleted) {
			uploadOrganization();
		}
	}, [organizationInputCompleted]);

	return (
		<div className="add-product-screen-container">
			<span className="add-product-page-title">{"Edit Organization"}</span>
			<Form className="add-product-form" autoComplete="off">
				<span className="add-product-form-title">{"Organization Details"}</span>
				<Form.Item
					className="login-form-item"
					rules={[{ required: true, message: "Please select an Organization Type." }]}
					required
					validateTrigger="onBlur"
				>
					<Select
						value={type || undefined}
						className="add-product-input-select"
						onChange={value => setType(value)}
						options={Object.values(OrganizationType).map(element => {
							return { value: element, label: element };
						})}
						placeholder="Organization Type"
						allowClear
						disabled={true}
						suffixIcon={
							<img
								src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
								alt="dropdown icon"
								className="chevron-button"
							/>
						}
					/>
				</Form.Item>
				{isAuthority ? (
					<Form.Item
						className="login-form-item"
						rules={[{ required: true, message: "Please select the jurisdiction." }]}
						required
						validateTrigger="onBlur"
					>
						<Select
							value={jurisdictionId || undefined}
							className="add-product-input-select"
							onChange={value => setJurisdictionId(value)}
							options={(jurisdictionsData?.jurisdictions ?? []).map(jurisdiction => ({
								value: jurisdiction.id,
								label: jurisdiction.name
							}))}
							placeholder="Jurisdiction"
							showSearch
							optionFilterProp="label"
							allowClear
							disabled={!canEditJurisdiction || loadingJurisdictions}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</Form.Item>
				) : null}
				{isAdminUser ? (
					<Form.Item className="login-form-item">
						<Select
							value={navProfile || undefined}
							className="add-product-input-select"
							onChange={value => setNavProfile(value || "")}
							options={[...NAV_PROFILE_OPTIONS]}
							placeholder="Navigation profile (empty: decided by the role of each user)"
							allowClear
							disabled={false}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									style={{ width: "1em", height: "1em" }}
									className="chevron-button"
								/>
							}
						/>
					</Form.Item>
				) : null}

				<Form.Item
					className="login-form-item"
					rules={[{ required: true, message: "Please input the organization name." }]}
					required
					validateTrigger="onBlur"
				>
					<Input
						name="name"
						placeholder="Name"
						className="add-product-input-pair"
						value={name}
						autoComplete="off"
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							setName(event.target.value);
						}}
						allowClear
						disabled={isLoading}
					/>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<Input
						name="name"
						placeholder="Contact Name (Optional)"
						className="add-product-input-pair"
						value={contactName}
						autoComplete="off"
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							setContactName(event.target.value);
						}}
						allowClear
						disabled={isLoading}
					/>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<Input
						name="name"
						placeholder="Contact Email (Optional)"
						className="add-product-input-pair"
						value={email}
						autoComplete="off"
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							setEmail(event.target.value);
						}}
						allowClear
						disabled={isLoading}
					/>
				</Form.Item>

				{/* Authorities don't order devices, so customer-facing pricing doesn't apply to them. */}
				{!isAuthority && canSeePricingToggle && (
					<div className="login-form-item">
						<span className="switch-label">Display customer-facing pricing for product orders</span>
						<Switch checked={displayPrice} onChange={checked => setDisplayPrice(checked)} disabled={isLoading} />
					</div>
				)}

				<div className="organization-button-container">
					<Button className="upload-product-button" onClick={() => setAddOrgPhotoModalOpen(true)} disabled={isLoading}>
						Upload a logo (Optional)
					</Button>
				</div>

				{orgPhotoThumbUrl ? (
					<div className="organization-button-container">
						<Image width={170} src={orgPhotoThumbUrl} />{" "}
						<img
							alt={`edit-attachment-icon`}
							src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
							className="selected-file-icon"
							onClick={() => {
								if (!isLoading) {
									handleOnEditPhoto();
								}
							}}
						/>
						<img
							alt={`remove-organization-photo-icon`}
							src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`}
							className="organization-photo-action-icon"
							onClick={() => {
								if (!isLoading) {
									handleOnRemovePhoto();
								}
							}}
						/>
					</div>
				) : null}

				<UploadPhotoModal
					open={addOrgPhotoModalOpen}
					onCancel={() => setAddOrgPhotoModalOpen(false)}
					uploadMethod={handleOnConfirmPhoto}
					aspectSlider
					disableCrop
				/>
			</Form>

			{/* <div className="add-product-form">
        <span className="page-title">{"Organization Properties"}</span>
        <Dropdown
          menu={{
            items: propertiesOptions,
            selectable: true,
            multiple: true,
            onSelect: (args) => handleOnSelectProperties(args),
            onDeselect: (args) => handleOnSelectProperties(args),
            selectedKeys: selectedPropertiesKeys,
          }}
          arrow
          className="organization-properties-dropdown"
          overlayClassName="products-dropdown-overlay"
          trigger={["click"]}
          open={propertiesDropdownOpen}
          onOpenChange={(
            open: boolean,
            info: { source: "trigger" | "menu" }
          ) => {
            if (info.source === "trigger" && open === false) {
              setPropertiesDropdownOpen(false);
            }
          }}
          disabled={isLoading}
        >
          <Button
            onClick={() => {
              setPropertiesDropdownOpen((previousValue) => !previousValue);
            }}
            disabled={isLoading}
          >
            Select Properties <DownOutlined />
          </Button>
        </Dropdown>

        <div className="organization-selected-properties-container">
          {selectedProperties.map((property: Properties) => {
            return (
              <div key={property.id} className="organization-selected-property">
                <span className="organization-selected-property-name">
                  {property.name}
                </span>
                <span
                  className="organization-selected-property-delete"
                  onClick={() => {
                    if (!isLoading) {
                      handleOnRemoveProperty(property.id);
                    }
                  }}
                >
                  {"X"}
                </span>
              </div>
            );
          })}
        </div>
      </div> */}

			<div className="add-product-button-container">
				<Button className="admin-add-product-button" htmlType="submit" onClick={handleOnSubmit} disabled={isLoading}>
					Save
				</Button>
			</div>
			<LoadingOverlay isLoading={isLoading} />
		</div>
	);
};

export default OrganizationEdit;
